import { HttpError, apiDownloadFile, apiFetchJson } from "@/services/apiClient";
import { API_URL } from "@/lib/utils";


export const PRE2022_SOURCES = [
  "Consol adjustments",
  "Opening balances file",
] as const;
export type Pre2022Source = (typeof PRE2022_SOURCES)[number];

export interface XeroConnection {
  tenant_id: string;
  tenant_name: string;
  tenant_type: string;
  entity: string | null;
  retrieval_date: string | null;
  pre2022_source: Pre2022Source | null;
}

export interface XeroStatus {
  connected: boolean;
  reason: string | null;
  scope: string | null;
  connections: XeroConnection[];
  connected_at: string | null;
  connected_by: string | null;
}

export const XERO_STATUS_QUERY_KEY = ["xero", "status"] as const;

export function getXeroStatus(): Promise<XeroStatus> {
  return apiFetchJson<XeroStatus>(`${API_URL}/integrations/xero/status`);
}

export async function startXeroConnect(): Promise<void> {
  const { authorize_url } = await apiFetchJson<{ authorize_url: string }>(
    `${API_URL}/integrations/xero/connect`,
  );
  window.location.href = authorize_url;
}

export interface XeroOrganisationProfilePayload {
  entity: string | null;
  retrieval_date: string | null;
  pre2022_source: Pre2022Source | null;
  default_legacy_office: string | null;
  required_in_cube: boolean;
  tenant_name?: string;
}

export interface XeroOrganisationProfile extends XeroOrganisationProfilePayload {
  row_id: string;
  tenant_name: string;
  taken_from: string | null;
  updated_at: string;
}

export function saveXeroOrganisationProfile(
  rowId: string,
  payload: XeroOrganisationProfilePayload,
): Promise<XeroOrganisationProfile> {
  return apiFetchJson<XeroOrganisationProfile>(
    `${API_URL}/integrations/xero/organisations/rules/${encodeURIComponent(rowId)}`,
    { method: "PUT", body: JSON.stringify(payload) },
  );
}

export interface XeroOrgRulesOrganisation {
  tenant_id: string | null;
  tenant_name: string;
  row_id: string | null;
  entity: string | null;
  retrieval_date: string | null;
  pre2022_source: Pre2022Source | null;
  default_legacy_office: string | null;
  required_in_cube: boolean;
  taken_from: string | null;
  updated_at: string | null;
}

export interface XeroOrgRulesSummary {
  generated_at: string;
  organisations: XeroOrgRulesOrganisation[];
  org_rules_error: string | null;
}

export const XERO_ORG_RULES_QUERY_KEY = ["xero", "organisations", "rules"] as const;

export function getXeroOrgRules(): Promise<XeroOrgRulesSummary> {
  return apiFetchJson<XeroOrgRulesSummary>(
    `${API_URL}/integrations/xero/organisations/rules`,
  );
}

export function exportXeroOrgRules(): Promise<void> {
  return apiDownloadFile(
    `${API_URL}/integrations/xero/organisations/rules/export`,
    "Xero Organisations.xlsx",
  );
}

export interface OrgRulesBulkImportResult {
  updated_count: number;
  updated_tenant_names: string[];
}

export function bulkImportXeroOrgRules(file: File): Promise<OrgRulesBulkImportResult> {
  return uploadExcelFile<OrgRulesBulkImportResult>(
    `${API_URL}/integrations/xero/organisations/rules/bulk-import`,
    file,
  );
}


export interface DimCubeMappingPayload {
  legacy_office: string | null;
  legacy_office_reported: string | null;
  site: string | null;
  legacy_location_reported: string | null;
  mapping_perimeter: string | null;
  xero_inclusion_date: string | null;
  legacy_office_fetched?: string;
}

export interface DimCubeMapping extends DimCubeMappingPayload {
  row_id: string | null;
  legacy_office_fetched: string;
  updated_at: string | null;
}

export interface DimCubeMappingsSummary {
  generated_at: string;
  mappings: DimCubeMapping[];
  mappings_error: string | null;
}

export const XERO_DIM_CUBE_MAPPINGS_QUERY_KEY = ["xero", "mappings"] as const;

export function getDimCubeMappings(): Promise<DimCubeMappingsSummary> {
  return apiFetchJson<DimCubeMappingsSummary>(
    `${API_URL}/integrations/xero/mappings`,
  );
}

export function saveDimCubeMapping(
  rowId: string,
  payload: DimCubeMappingPayload,
): Promise<DimCubeMapping> {
  return apiFetchJson<DimCubeMapping>(
    `${API_URL}/integrations/xero/mappings/${encodeURIComponent(rowId)}`,
    { method: "PUT", body: JSON.stringify(payload) },
  );
}

export function exportDimCubeMappings(): Promise<void> {
  return apiDownloadFile(
    `${API_URL}/integrations/xero/mappings/export`,
    "Xero Cube Mappings.xlsx",
  );
}

export type XeroChangeLogSource = "edit" | "bulk_import" | "cascade" | "seed";

export interface XeroChangeLogFieldChange {
  field: string;
  old: string | boolean | null;
  new: string | boolean | null;
}

export interface XeroChangeLogEntry {
  id: string;
  row_id: string;
  label: string;
  at: string;
  by: string;
  source: XeroChangeLogSource;
  changes: XeroChangeLogFieldChange[];
}

export interface XeroChangeLogPage {
  entries: XeroChangeLogEntry[];
  next_cursor: string | null;
  retention_days: number;
}

export interface XeroChangeLogParams {
  limit?: number;
  cursor?: string | null;
  search?: string;
  source?: XeroChangeLogSource;
}

export const XERO_ORG_RULES_LOGS_QUERY_KEY = ["xero", "organisations", "rules", "logs"] as const;
export const XERO_DIM_CUBE_MAPPINGS_LOGS_QUERY_KEY = ["xero", "mappings", "logs"] as const;

function changeLogUrl(path: string, params: XeroChangeLogParams): string {
  const query = new URLSearchParams();
  if (params.limit) query.set("limit", String(params.limit));
  if (params.cursor) query.set("cursor", params.cursor);
  if (params.search) query.set("search", params.search);
  if (params.source) query.set("source", params.source);
  const qs = query.toString();
  return `${API_URL}/integrations/xero/${path}${qs ? `?${qs}` : ""}`;
}

export function getXeroOrgRulesLogs(params: XeroChangeLogParams = {}): Promise<XeroChangeLogPage> {
  return apiFetchJson<XeroChangeLogPage>(changeLogUrl("organisations/rules/logs", params));
}

export function getDimCubeMappingsLogs(params: XeroChangeLogParams = {}): Promise<XeroChangeLogPage> {
  return apiFetchJson<XeroChangeLogPage>(changeLogUrl("mappings/logs", params));
}


export type XeroRunStatus = "started" | "succeeded" | "failed" | "skipped";

export interface XeroWatermark {
  tenant_id: string;
  tenant_name: string;
  dataset: string;
  watermark_journal_number: number | null;
  last_run_at: string | null;
  last_success_at: string | null;
  last_status: XeroRunStatus | null;
  rows_ingested: number;
}

export type XeroRunReason =
  | "loaded"
  | "no_new_journals"
  | "daily_limit_reached"
  | "token_problem"
  | "error"
  | "not_selected"
  | "not_attempted"
  | "in_progress"
  | "not_reported"
  | "pending"
  | "not_granted";

export interface XeroRunOrganisationOutcome {
  tenant_id: string;
  tenant_name: string;
  entity: string | null;
  reason: XeroRunReason;
}

export interface XeroRun {
  run_id: string;
  tenant_id: string;
  tenant_name: string;
  dataset: string;
  status: XeroRunStatus;
  started_at: string;
  finished_at: string | null;
  rows_ingested: number;
  pages_fetched: number;
  watermark_journal_number: number | null;
  token_refreshed: boolean;
  error_message: string | null;
  notebook: string | null;
  reason: XeroRunReason | null;
  api_calls: number | null;
  day_limit_remaining: number | null;
  min_limit_remaining: number | null;
}

export interface XeroOrchestrationSummary {
  connected: boolean;
  reason: string | null;
  expires_at: string | null;
  expires_in: number;
  organisations: number;
  watermarks: XeroWatermark[];
  recent_runs: XeroRun[];
}

export const XERO_ORCHESTRATION_QUERY_KEY = ["xero", "orchestration"] as const;

export function getXeroOrchestration(
  dataset = "journals",
): Promise<XeroOrchestrationSummary> {
  return apiFetchJson<XeroOrchestrationSummary>(
    `${API_URL}/integrations/xero/orchestration?dataset=${encodeURIComponent(dataset)}`,
  );
}



export type XeroPipelineMode = "incremental" | "backfill";
export type XeroTriggerStatus =
  | "waiting"
  | "queued"
  | "running"
  | "completed"
  | "failed"
  | "cancelled"
  | "unknown";

export function isXeroRunInFlight(status: XeroTriggerStatus | undefined): boolean {
  return status === "queued" || status === "running";
}

export function isXeroRunActive(status: XeroTriggerStatus | undefined): boolean {
  return status === "waiting" || isXeroRunInFlight(status);
}

export interface XeroPipelineTriggerPayload {
  mode: XeroPipelineMode;
  tenant_ids: string[];
  dataset?: string;
  queue_if_busy?: boolean;
}


export interface XeroPipelineActiveRun {
  source: "prism" | "fabric";
  status: XeroTriggerStatus;
  run_id: string | null;
  mode: XeroPipelineMode | null;
  triggered_at: string | null;
  started_at: string | null;
  invoke_type: string | null;
  started_by_you: boolean;
}

export interface XeroPipelineBusy {
  reason: "already_running" | "queue_full";
  message: string;
  running: XeroPipelineActiveRun;
  queued: {
    run_id: string;
    mode: XeroPipelineMode;
    triggered_at: string;
    queued_by_you: boolean;
  } | null;
  can_queue: boolean;
}

export function readPipelineBusy(error: unknown): XeroPipelineBusy | null {
  if (!(error instanceof HttpError) || error.status !== 409) return null;
  try {
    const body = JSON.parse(error.message.replace(/^HTTP \d+: /, ""));
    const detail = body?.detail;
    return detail && typeof detail === "object" && detail.running ? (detail as XeroPipelineBusy) : null;
  } catch {
    return null;
  }
}

export interface XeroPipelineRunSummary {
  organisations: number;
  started: number;
  succeeded: number;
  failed: number;
  skipped: number;
  rows_ingested: number;
  granted: number;
  not_selected: number;
  not_attempted: number;
  pending: number;
  not_granted: number;
  stopped_early: boolean;
}

export interface XeroPipelineRun {
  run_id: string;
  mode: XeroPipelineMode;
  dataset: string;
  tenant_ids: string[];
  full_reload: boolean;
  status: XeroTriggerStatus;
  failure_reason: string | null;
  fabric_job_instance_id: string | null;
  triggered_by: string | null;
  triggered_at: string;
  dispatched_at: string | null;
  started_at: string | null;
  finished_at: string | null;
  last_polled_at: string | null;
  can_cancel: boolean;
  poll_error: string | null;
  summary: XeroPipelineRunSummary;
  organisations: XeroRun[];
  unreported_organisations: XeroRunOrganisationOutcome[];
  reconcile_error: string | null;
}

export const XERO_PIPELINE_RUNS_QUERY_KEY = ["xero", "pipeline", "runs"] as const;

export const xeroPipelineRunQueryKey = (runId: string) =>
  ["xero", "pipeline", "run", runId] as const;

export function triggerXeroPipeline(
  payload: XeroPipelineTriggerPayload,
): Promise<XeroPipelineRun> {
  return apiFetchJson<XeroPipelineRun>(`${API_URL}/integrations/xero/pipeline/runs`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function listXeroPipelineRuns(limit = 10): Promise<XeroPipelineRun[]> {
  return apiFetchJson<XeroPipelineRun[]>(
    `${API_URL}/integrations/xero/pipeline/runs?limit=${limit}`,
  );
}

export function getXeroPipelineRun(runId: string): Promise<XeroPipelineRun> {
  return apiFetchJson<XeroPipelineRun>(
    `${API_URL}/integrations/xero/pipeline/runs/${encodeURIComponent(runId)}`,
  );
}

export function cancelXeroPipelineRun(runId: string): Promise<XeroPipelineRun> {
  return apiFetchJson<XeroPipelineRun>(
    `${API_URL}/integrations/xero/pipeline/runs/${encodeURIComponent(runId)}/cancel`,
    { method: "POST" },
  );
}



export type XeroQuotaSource = "day_limit_header" | "api_calls" | "pages_fetched";

export interface XeroOrganisationQuota {
  tenant_id: string;
  tenant_name: string;
  entity: string | null;
  limit: number;
  calls_used: number;
  calls_remaining: number;
  percent_used: number;
  estimated: boolean;
  source: XeroQuotaSource;
  pages_fetched: number;
  api_calls: number | null;
  day_limit_remaining: number | null;
  day_limit_observed_at: string | null;
  runs_in_window: number;
  last_run_at: string | null;
  limit_hits_in_window: number;
}

export interface XeroQuotaSummary {
  connected: boolean;
  reason: string | null;
  generated_at: string;
  window_hours: number;
  window_start: string;
  limit: number;
  organisations: XeroOrganisationQuota[];
}

export const XERO_QUOTA_QUERY_KEY = ["xero", "quota"] as const;

export function getXeroQuota(): Promise<XeroQuotaSummary> {
  return apiFetchJson<XeroQuotaSummary>(`${API_URL}/integrations/xero/quota`);
}


export interface XeroLakehouseOrganisation {
  tenant_id: string;
  tenant_name: string;
  entity: string | null;
  bronze_rows: number;
  journals: number;
  max_journal_number: number | null;
  earliest_journal_date: string | null;
  latest_journal_date: string | null;
  last_ingested_at: string | null;
}

export interface XeroLakehouseSummary {
  generated_at: string;
  bronze_table: string;
  bronze_rows: number;
  organisations: XeroLakehouseOrganisation[];

  bronze_error: string | null;  stale: boolean;
}

export const XERO_LAKEHOUSE_QUERY_KEY = ["xero", "lakehouse"] as const;

export function getXeroLakehouse(refresh = false): Promise<XeroLakehouseSummary> {
  return apiFetchJson<XeroLakehouseSummary>(
    `${API_URL}/integrations/xero/lakehouse${refresh ? "?refresh=true" : ""}`,
  );
}

export interface MappingBulkImportResult {
  updated_count: number;
  updated_legacy_office_fetched: string[];
}

async function readBulkImportError(res: Response): Promise<string> {
  try {
    const body = await res.json();
    if (typeof body?.detail === "string") return body.detail;
    if (body?.detail?.message) return body.detail.message;
  } catch {
    /* ignore */
  }
  return "Bulk import failed.";
}


async function uploadExcelFile<T>(url: string, file: File): Promise<T> {
  const body = new FormData();
  body.append("file", file);

  const token = localStorage.getItem("access_token");
  const res = await fetch(url, {
    method: "POST",
    body,
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });

  if (res.status === 401) {
    window.dispatchEvent(new Event("auth:logout"));
    throw new Error("Your session expired. Please sign in again.");
  }
  if (!res.ok) throw new Error(await readBulkImportError(res));
  return res.json();
}

export function bulkImportDimCubeMappings(file: File): Promise<MappingBulkImportResult> {
  return uploadExcelFile<MappingBulkImportResult>(
    `${API_URL}/integrations/xero/mappings/bulk-import`,
    file,
  );
}
